import React, { useState, useEffect } from "react";
import {
  Dialog,
  IconButton,
  CircularProgress,
  Button,
  TextField,
  Box,
  Typography,
  makeStyles,
  Theme,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";

import { NoteTypeSection } from "./NoteTypeSection";
import { OwnerSection } from "./OwnerSection";
import { TaggedUsersSection } from "./TaggedUsersSection";
import { LabelsSection } from "./LabelsSection";
import { NotesAttached } from "./NotesAttached";

import { useComments, useNote } from "../../../../common/CommonQueries";
import { DispositionSection } from "./DispositionSection";
import { FileSection } from "./FileSection";
import { ViewAttachmentsModal } from "./ViewAttachmentsModal";
import { toast } from "react-toastify";
import { useFetchContext } from "../../../../common/FetchContext";
import {
  useLinkFileToNote,
  useUpdateNote,
} from "../../../../common/CommonMutations";
import { formatDate } from "../../../../common/formatDate";
import { usePostFile } from "../../../../common/services/fileServices";

const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    borderRadius: 12,
    overflow: "hidden",
    maxHeight: "90vh",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: "1.1rem",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  noteIdBadge: {
    backgroundColor: "#e08c00",
    color: "#fff",
    fontWeight: 600,
    borderRadius: 6,
    padding: "2px 10px",
  },
  contentGrid: {
    display: "flex",
    flexDirection: "row",
    minHeight: 520,
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  leftColumn: {
    flex: 1,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflowY: "auto",
  },
  rightColumn: {
    width: 320,
    backgroundColor: "#f9f9fb",
    borderLeft: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
    overflowY: "auto",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      borderLeft: "none",
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  commentBox: {
    backgroundColor: "#f4f4f4",
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
  },
  commentContainer: {
    marginBottom: theme.spacing(2.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(2.5),
    "&:last-child": {
      borderBottom: "none",
    },
  },
  commentHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  commentAuthor: {
    fontWeight: 500,
    fontSize: "13px",
    color: "#16191D",
  },
  commentDate: {
    fontSize: "11px",
    color: "#7B8794",
  },
  commentBubble: {
    backgroundColor: "#f7f7f7",
    borderRadius: 12,
    padding: theme.spacing(1.5, 2),
  },
  commentText: {
    fontSize: "14px",
    color: "#4A545F",
  },
  replySection: {
    marginTop: theme.spacing(2),
  },
  replyInput: {
    backgroundColor: "#fff",
    borderRadius: 8,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
  footerActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(2),
  },
  commentBtn: {
    backgroundColor: "#e08c00",
    color: "#fff",
    textTransform: "none",
    fontWeight: 500,
    padding: "6px 20px",
    borderRadius: 6,
    "&:hover": {
      backgroundColor: "#c77c00",
    },
  },
  closeBtn: {
    textTransform: "none",
    fontWeight: 500,
    borderRadius: 6,
    padding: "6px 20px",
    color: "#555",
  },
  sidebarLabel: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "#888",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    marginBottom: theme.spacing(0.5),
  },
  attachmentsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));

export const NoteDetailModal = ({ noteId, open, onClose }: any) => {
  const classes = useStyles();

  // Fetch note details and comments thread

  const { authAxios } = useFetchContext();
  const {
    data: note,
    isLoading: isLoadingNote,
    refetch: refetchNote,
  } = useNote(noteId);
  const {
    data: comments = [],
    isLoading: isLoadingComments,
    refetch: refetchComments,
  } = useComments(noteId);
  const { mutateAsync: mutateNote, isLoading: isUpdating } = useUpdateNote();

  // Local State
  const [disposition, setDisposition] = React.useState({ name: "", id: -1 });
  const [commentText, setCommentText] = useState("");
  const [usedLabels, setUsedLabels] = useState<any[]>([]);
  const [owner, setOwner] = useState<any>({ username: "", id: -1 });
  const [taggedUsers, setTaggedUsers] = useState<any[]>([]);
  const [selectedNoteType, setSelectedNoteType] = useState<number>(0);
  const [files, setFiles] = useState<any[]>([]);
  const { mutateAsync: mutatePostFile } = usePostFile();
  const { mutateAsync: mutateLinkFile } = useLinkFileToNote();
  const resetState = () => {
    setDisposition({ name: "", id: -1 });
    setCommentText("");
    setUsedLabels([]);
    setOwner({ username: "", id: -1 });
    setTaggedUsers([]);
    setSelectedNoteType(0);
    setFiles([]);
  };
  const handleCopyNoteId = async () => {
    try {
      await navigator.clipboard.writeText(String(noteId));
      toast.success("Note ID copied");
    } catch (error) {
      toast.error("Failed to copy Note ID");
    }
  };
  // Populate local states once note query resolves
  useEffect(() => {
    if (note) {
      if (note.owner) setOwner({ username: note.owner_name, id: note.owner });
      if (note.disposition)
        setDisposition({ name: note.disposition_name, id: note.disposition });
      if (note.tagged_users) setTaggedUsers(note.tagged_users);
      if (note.labels) setUsedLabels(note.labels);
      if (note.note_type) setSelectedNoteType(note.note_type);
    }
  }, [note]);

  if (!open || !noteId) return null;

  const isLoading = isLoadingNote || isLoadingComments;

  const updateNote = async (overrideCommentText?: string) => {
    try {
      const newLabels = usedLabels.map((label) => label.id);
      const alertUsers = taggedUsers.map((user) => user.id);
      const activeComment =
        overrideCommentText !== undefined ? overrideCommentText : commentText;
      const hasComment = activeComment.trim().length > 0;

      const payload = {
        authAxios,
        id: noteId,
        owner: owner.id,
        disposition: disposition.id,
        labels: newLabels,
        tagged_users: alertUsers,
        note_type: selectedNoteType,
        comment: hasComment
          ? {
              text: activeComment,
              subject: `Note Id: ${note.id} \n Parent Note Type: ${note.note_type_name || ""} \n Comment`,
              note_type: 1,
              parent_note: note.id,
            }
          : null,
      };

      const newNote = await mutateNote(payload);

      for (const file of files) {
        const newFile = await mutatePostFile({ authAxios, file });
        await mutateLinkFile({
          authAxios,
          fileId: newFile.id,
          noteId: newNote.id,
        });
      }

      setCommentText("");
      resetState();

      toast.success("note updated.");
      
      await onClose();

      // Refresh component data
      refetchNote();
      refetchComments();
    } catch (error) {
      toast.error(`Error occurred while updating: ${error}`);
    }
  };

  const handleCommentSubmit = () => {
    if (!commentText.trim()) return;
    updateNote();
  };

  const handleClose = () => {
    resetState();
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      {/* Header Bar */}
      <Box className={classes.headerContainer}>
        <Typography className={classes.headerTitle}>Note</Typography>
        <Box className={classes.headerRight}>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginRight: 4 }}
          >
            Note ID:
          </Typography>
          <Box
            className={classes.noteIdBadge}
            onClick={handleCopyNoteId}
            title="Copy Note ID"
            style={{ cursor: "pointer" }}
          >
            {noteId}
          </Box>
          <IconButton size="small"   onClick={handleClose} style={{ marginLeft: 8 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Main Container */}
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={400}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box className={classes.contentGrid}>
          {/* Left Column (Title, Note Body, Dynamic Comments, Reply Box) */}
          <Box className={classes.leftColumn}>
            <Box>
              <Typography
                variant="h5"
                style={{
                  fontWeight: 500,
                  marginBottom: 16,
                  fontSize: "19px",
                  color: "#16191D",
                }}
              >
                {note?.subject || ""}
              </Typography>

              {/* Main Note Item */}
              <Box className={classes.commentContainer}>
                <Box className={classes.commentHeader}>
                  <Typography className={classes.commentAuthor}>
                    {note?.username || ""}
                  </Typography>
                  <Typography className={classes.commentDate}>
                    {formatDate(note?.datetime) || ""}
                  </Typography>
                </Box>
                <Box className={classes.commentBubble}>
                  <Typography className={classes.commentText}>
                    {note?.text || ""}
                  </Typography>
                </Box>
              </Box>

              {/* Dynamic Comments List */}
              {comments.map((comment: any) => (
                <Box key={comment.id} className={classes.commentContainer}>
                  <Box className={classes.commentHeader}>
                    <Typography className={classes.commentAuthor}>
                      {comment.username}
                    </Typography>
                    <Typography className={classes.commentDate}>
                      {formatDate(comment.datetime) || comment.datetime}
                    </Typography>
                  </Box>
                  <Box className={classes.commentBubble}>
                    <Typography className={classes.commentText}>
                      {comment.text}
                    </Typography>
                  </Box>
                </Box>
              ))}

              {/* Reply Input Box */}
              <Box className={classes.replySection}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Add a reply..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  className={classes.replyInput}
                />
              </Box>
            </Box>

            {/* Footer Bar */}
            <Box className={classes.footerActions}>
              <Typography variant="caption" color="textSecondary"></Typography>
              <Box display="flex" style={{ gap: 8 }}>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  className={classes.closeBtn}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<SendIcon style={{ fontSize: 16 }} />}
                  onClick={handleCommentSubmit}
                  className={classes.commentBtn}
                >
                  Comment
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Right Sidebar (Metadata Inputs) */}
          <Box className={classes.rightColumn}>
            {/* Attached To */}
            <Box>
              <Typography className={classes.sidebarLabel}>
                ATTACHED TO
              </Typography>
              <NotesAttached
                attached={note?.parent_objects || []}
                isNew={false}
              />
            </Box>

            {/* Type Of Note */}
            <Box>
              <Typography className={classes.sidebarLabel}>
                TYPE OF NOTE
              </Typography>
              <NoteTypeSection
                noteType={selectedNoteType}
                setNoteType={setSelectedNoteType}
              />
            </Box>

            {/* Owner Section */}
            <Box>
              <Typography className={classes.sidebarLabel}>OWNER</Typography>
              <OwnerSection owner={owner} setOwner={setOwner} isNew={false} />
            </Box>

            {/* Tagged Users Section */}
            <Box>
              <Typography className={classes.sidebarLabel}>
                TAGGED USERS
              </Typography>
              <TaggedUsersSection
                taggedUsers={taggedUsers}
                setTaggedUsers={setTaggedUsers}
                isNew={false}
              />
            </Box>

            {/* Labels Section */}
            <Box>
              <Typography className={classes.sidebarLabel}>LABELS</Typography>
              <LabelsSection
                usedLabels={usedLabels}
                setUsedLabels={setUsedLabels}
                isNew={false}
              />
            </Box>
            <Box>
              <Typography className={classes.sidebarLabel}>STATUS</Typography>
              <DispositionSection
                disposition={disposition}
                setDisposition={setDisposition}
                isNew={false}
              />
            </Box>

            <Box>
              <Typography className={classes.sidebarLabel}>
                ATTACHMENTS
              </Typography>
              <Box className={classes.attachmentsContainer}>
                {/* 1. File Upload Dropzone Section */}
                <FileSection
                  files={files}
                  setFiles={setFiles}
                  isNew={false}
                  noteId={noteId}
                />

                {/* 2. Attachment Viewer Component */}
                <ViewAttachmentsModal files={note?.attachments ?? []} />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Dialog>
  );
};
